import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faTimes } from '@fortawesome/free-solid-svg-icons';

import { getRecapBDNs, getRecapBDNDocument } from '../services/invoicingService';
import './RecapInvoicesPanel.css';

/**
 * The Bunker Delivery Notes for this deal, listed on the SALES recap.
 *
 * PREMISE: the BDN lives on the DELIVERY, and a customer trader has no
 * delivery access at all. It reaches this panel through the RECAP's door
 * (``/api/invoicing/recaps/<id>/bdns/``, gated on recap visibility), exactly
 * as the invoices panel does -- never through a delivery endpoint, which would
 * refuse a customer trader by design.
 *
 * Not a customer-trader feature: everyone who can open the sales recap sees
 * it. The customer-trader lock already decides WHICH recaps they can open.
 *
 * Reuses ``RecapInvoicesPanel.css`` on purpose so the two panels on one recap
 * cannot drift apart visually.
 */

/**
 * The server's own words out of a failed BLOB request.
 *
 * With ``responseType: 'blob'`` an error body arrives as a Blob, so reading
 * ``err.response.data.detail`` straight off it yields undefined and the panel
 * would fall back to a generic message -- hiding the one sentence that says
 * whether the archive is down or the document is not on this deal.
 */
const readBlobAsText = (blob) => (
  // ``Blob.text()`` where the browser has it, FileReader where it does not.
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsText(blob);
    })
);

const messageFromBlobError = async (err, fallback) => {
  const body = err?.response?.data;
  if (body instanceof Blob) {
    try {
      const parsed = JSON.parse(await readBlobAsText(body));
      if (parsed?.detail) return parsed.detail;
    } catch (ignored) {
      // Not JSON -- fall through to the generic message below.
    }
  }
  return body?.detail || err?.message || fallback;
};
const RecapBDNsPanel = ({ recapId }) => {
  const [rows, setRows] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  // The reason loading failed, in the server's own words. A caught error that
  // sets an empty list would render as "this deal has no BDNs", which is a
  // different and wrong statement.
  const [loadError, setLoadError] = useState(null);
  const [openError, setOpenError] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileName, setFileName] = useState('');

  const load = useCallback(async () => {
    if (!recapId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getRecapBDNs(recapId);
      setRows(data.results || []);
      setWarnings(data.warnings || []);
    } catch (err) {
      setLoadError(
        err?.response?.data?.detail
        || err?.message
        || 'Could not load the BDNs for this deal.'
      );
    } finally {
      setLoading(false);
    }
  }, [recapId]);

  useEffect(() => { load(); }, [load]);

  // Revoke the object URL on unmount so closing the recap does not leak it.
  useEffect(() => () => { if (fileUrl) window.URL.revokeObjectURL(fileUrl); }, [fileUrl]);

  const openDocument = useCallback(async (row) => {
    setOpeningId(row.document_id);
    setOpenError(null);
    try {
      const blob = await getRecapBDNDocument(recapId, row.document_id);
      setFileUrl((previous) => {
        if (previous) window.URL.revokeObjectURL(previous);
        return window.URL.createObjectURL(blob);
      });
      setFileName(row.display_name || `BDN ${row.bdn_number || row.document_id}`);
    } catch (err) {
      // A failure says what went wrong and leaves the row's button live, so
      // an archive outage can simply be tried again.
      setOpenError(await messageFromBlobError(
        err, 'Could not open that BDN document.',
      ));
    } finally {
      setOpeningId(null);
    }
  }, [recapId]);

  const closeDocument = () => {
    setFileUrl((previous) => {
      if (previous) window.URL.revokeObjectURL(previous);
      return null;
    });
    setFileName('');
  };

  if (!recapId) return null;

  return (
    <div className="recap-invoices-panel">
      <div className="recap-invoices-panel__header">
        <h2>Bunker Delivery Notes</h2>
        <span className="recap-invoices-panel__note">
          The BDNs recorded against this deal&apos;s deliveries.
        </span>
      </div>

      {loading && <p className="recap-invoices-panel__state">Loading BDNs…</p>}

      {!loading && loadError && (
        <div className="recap-invoices-panel__error" role="alert">
          <p>{loadError}</p>
          <button type="button" onClick={load}>Try again</button>
        </div>
      )}

      {/* The archive being unreachable is NOT an empty list: the server
          degrades to what it knows locally and says so, and the trader must
          read that sentence rather than assume nothing was delivered. */}
      {!loading && !loadError && warnings.map((warning) => (
        <p key={warning} className="recap-invoices-panel__state" role="status">
          {warning}
        </p>
      ))}

      {!loading && !loadError && rows.length === 0 && (
        <p className="recap-invoices-panel__state">
          No BDNs have been recorded against this deal yet.
        </p>
      )}

      {openError && (
        <div className="recap-invoices-panel__error" role="alert">
          <p>{openError}</p>
        </div>
      )}

      {!loading && !loadError && rows.length > 0 && (
        <table className="recap-invoices-panel__table">
          <thead>
            <tr>
              <th>BDN</th>
              <th>BDN date</th>
              <th>Quantity (MT)</th>
              <th>Delivery</th>
              <th>Document</th>
              <th aria-label="Open BDN" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.document_id}>
                <td>{row.bdn_number || '—'}</td>
                <td>{row.bdn_date || '—'}</td>
                <td>{row.quantity_mt ?? '—'}</td>
                <td>{row.delivery_unique_number || '—'}</td>
                <td>{row.display_name || '—'}</td>
                <td>
                  {row.can_download ? (
                    <button
                      type="button"
                      onClick={() => openDocument(row)}
                      disabled={openingId === row.document_id}
                    >
                      <FontAwesomeIcon icon={faFilePdf} />{' '}
                      {openingId === row.document_id ? 'Opening…' : 'View BDN'}
                    </button>
                  ) : (
                    /* HMS cannot prove this file belongs to the deal without
                       asking the archive, so the recap refuses to serve it.
                       Saying so beats a button that always fails. */
                    <span title="This BDN was filed before IMT recorded which delivery a document belongs to, so it can only be opened from the delivery.">
                      Not available here
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {fileUrl && (
        <div className="recap-invoices-panel__viewer">
          <div className="recap-invoices-panel__viewer-bar">
            <span>{fileName}</span>
            <button type="button" onClick={closeDocument} aria-label="Close preview">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <iframe title={fileName} src={fileUrl} />
        </div>
      )}
    </div>
  );
};

export default RecapBDNsPanel;
